import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { StockHistoryPoint } from '../../../types/inventory';

interface StockHistoryChartProps {
    data: StockHistoryPoint[];
    isLoading?: boolean;
}

function formatNumber(value: number): string {
    return new Intl.NumberFormat('de-CH', { maximumFractionDigits: 0 }).format(value);
}

function formatCompact(value: number): string {
    if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
    return value.toFixed(0);
}

function formatMonthShort(monthKey: string): string {
    // "2026-04" -> "Apr 26"
    const [y, m] = monthKey.split('-');
    const date = new Date(Number(y), Number(m) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

export function StockHistoryChart({ data, isLoading }: StockHistoryChartProps) {
    if (isLoading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
            </div>
        );
    }

    if (!data || data.length === 0) {
        return <div className="py-8 text-center text-sm text-gray-400">No stock history available.</div>;
    }

    return (
        <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <defs>
                    <linearGradient id="stockQtyGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                    dataKey="month"
                    tickFormatter={formatMonthShort}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    interval="preserveStartEnd"
                    minTickGap={24}
                />
                <YAxis
                    yAxisId="qty"
                    tickFormatter={formatCompact}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    width={55}
                />
                <YAxis
                    yAxisId="value"
                    orientation="right"
                    tickFormatter={(v) => `€${formatCompact(v)}`}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    width={60}
                />
                <Tooltip
                    labelFormatter={(label) => typeof label === 'string' ? formatMonthShort(label) : String(label ?? '')}
                    formatter={(value, name) => {
                        const num = typeof value === 'number' ? value : Number(value) || 0;
                        if (name === 'Stock Value') return [`€${formatNumber(num)}`, name];
                        return [formatNumber(num), name];
                    }}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area
                    yAxisId="qty"
                    type="monotone"
                    dataKey="qty"
                    name="Stock Qty"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fill="url(#stockQtyGradient)"
                />
                <Area
                    yAxisId="value"
                    type="monotone"
                    dataKey="valueEur"
                    name="Stock Value"
                    stroke="#10b981"
                    strokeWidth={1.5}
                    fill="transparent"
                    strokeDasharray="4 4"
                />
            </AreaChart>
        </ResponsiveContainer>
    );
}
